import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ALL_THEMES, THEME_LABEL_KEYS, useTheme, type ThemeName } from './ThemeContext';
import { useI18n } from '../i18n';
import { useAuth } from '../auth';
import './ThemeToggle.css';

export function ThemeToggle() {
  const { darkMode, theme, setTheme, isPremiumTheme } = useTheme();
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: PointerEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const selectTheme = (nextTheme: ThemeName) => {
    if (isPremiumTheme(nextTheme) && !user?.is_premium) {
      setOpen(false);
      navigate('/premium');
      return;
    }
    setTheme(nextTheme);
    setOpen(false);
  };

  return (
    <div className="theme-toggle-wrap" ref={wrapRef}>
      <button
        className="global-ctrl-btn theme-toggle-btn icon-btn"
        onClick={() => setOpen(prev => !prev)}
        title={t.theme.pickerTitle}
        aria-label={t.theme.pickerTitle}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {darkMode ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="5"/>
            <line x1="12" y1="1" x2="12" y2="3"/>
            <line x1="12" y1="21" x2="12" y2="23"/>
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
            <line x1="1" y1="12" x2="3" y2="12"/>
            <line x1="21" y1="12" x2="23" y2="12"/>
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
          </svg>
        )}
      </button>

      {open && (
        <div className="theme-menu" role="menu" aria-label={t.theme.pickerTitle}>
          <div className="theme-menu-desc">{t.theme.pickerDesc}</div>
          {ALL_THEMES.map(name => {
            const locked = isPremiumTheme(name) && !user?.is_premium;
            const active = theme === name;
            const label = (t.theme as Record<string, string>)[THEME_LABEL_KEYS[name]];
            return (
              <button
                key={name}
                className={`theme-menu-item ${active ? 'active' : ''}`}
                onClick={() => selectTheme(name)}
                title={locked ? t.theme.premiumLocked : label}
                role="menuitemradio"
                aria-checked={active}
              >
                <span className={`theme-menu-swatch theme-menu-swatch--${name}`} aria-hidden="true" />
                <span className="theme-menu-label">{label}</span>
                {locked ? (
                  <span className="theme-menu-mark" aria-hidden="true">🔒</span>
                ) : active ? (
                  <span className="theme-menu-mark" aria-hidden="true">✓</span>
                ) : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
